import React, {useEffect, useState} from 'react';
import {Box, Button, CircularProgress, Grid, Stack, TextField, Tooltip, Typography} from "@mui/material";
import {useProjectData} from "../../hooks/useProjectData";
import {Link as RouterLink, useNavigate, useSearchParams} from "react-router-dom";
import PrecheckPanel from "./PrecheckPanel";
import {CustomModelRequests} from "../../api/requests/CustomModelRequests";
import AsyncButton from "../button/AsyncButton";
import PageHeader from "../layouts/PageHeader";
import CustomModelFormOpenAi from "./CustomModelFormOpenAi";
import GroupedModelSelect from "../models/GroupedModelSelect";
import {AnyCustomModelOptions} from "../../api/json/CustomModelOptionsJson";
import LaunchIcon from "@mui/icons-material/Launch";
import {useIntegrations} from "../../hooks/useIntegrations"
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ApiError from "../../api/ApiError"
import {useModelSpecs} from "../../hooks/useModelSpecs"
import {ModelPlatform} from "../../api/enum/ModelPlatform"
import {AnyModelSpecJson, ModelSpecOpenAiJson, ModelSpecTogetherJson} from "../../api/json/ModelSpecJson"
import TemplateSelect from "../templates/TemplateSelect"
import NewTemplateModal from "../templates/NewTemplateModal"
import AddIcon from "@mui/icons-material/Add"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import animations from "../../Animations.module.scss"
import {useBreadcrumbs} from "../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../library/NavLabels"
import TemplateRenderer from "../templates/TemplateRenderer"
import ErrorMessage from "../error/ErrorMessage"
import CustomModelFormTogether from "./CustomModelFormTogether"

function TrainCustomModelPage() {
    const {id, project, reloadProject} = useProjectData()
    useBreadcrumbs([
        {label: NAV_LABELS.MODELS, url: `/projects/${id}/models`},
        {label: NAV_LABELS.TRAIN_CUSTOM_MODEL},
    ])

    const [name, setName] = useState<string | null>(null)

    const [selectedModel, setSelectedModel] = useState<AnyModelSpecJson>()
    const [platform, setPlatform] = useState<ModelPlatform>()
    const [template, setTemplate] = useState<AnyTemplateJson>()
    const [options, setOptions] = useState<AnyCustomModelOptions>()
    const [blocked, setBlocked] = useState(true)
    const [formValid, setFormValid] = useState(false)

    const [saveInProgress, setSaveInProgress] = useState(false)
    const [saveError, setSaveError] = useState<ApiError>()

    const [newTemplateModalOpen, setNewTemplateModalOpen] = useState(false)

    const navigate = useNavigate()

    const {integrationsLoading, integrations, integrationsError} = useIntegrations()

    const {trainableModels, modelsError} = useModelSpecs()

    // Get the unique platform values from the trainable models array
    let customModelPlatforms = Array.from(new Set(trainableModels?.map(modelSpec => modelSpec.platform)))

    // Filter the custom model platforms to ones that are set up as integrations
    customModelPlatforms = customModelPlatforms.filter(platform => integrations?.find(integration => integration.platform === platform))

    const [searchParams] = useSearchParams()

    useEffect(() => {
        const templateId = searchParams.get("templateId")
        if (project && templateId) {
            const template = project.templates.find(t => t.id == templateId)
            if (template) {
                setTemplate(template)
            }
        }
    }, [searchParams])

    useEffect(() => {
        if (name && name.length > 0) return
        CustomModelRequests.getName().then(obj => setName(obj.name))
    }, [])

    useEffect(() => reloadProject(), [])

    const handleModelSelected = (model?: AnyModelSpecJson) => {
        setSelectedModel(model)
        if (model && model.platform !== platform) {
            setPlatform(model.platform)
            setOptions(undefined)
            setFormValid(true)
        }
    }

    const handleCreateFinetune = () => {
        if (!name || !project || !platform || !options) return

        setSaveInProgress(true)
        CustomModelRequests.create(project.id, name, options)
            .then(() => {
                reloadProject()
                navigate(`/projects/${project.id}/models`)
            })
            .catch(setSaveError)
            .finally(() => setSaveInProgress(false))
    }

    if (!project) {
        return <></>
    }

    if (integrationsLoading || name === null) {
        return <CircularProgress/>
    }

    if (integrationsError || modelsError) {
        return (
            <PageHeader pageTitle="New Custom Model" sx={{pb: 0}}>
                <ErrorMessage error={integrationsError ?? modelsError!}/>
            </PageHeader>
        )
    }

    if (!customModelPlatforms || customModelPlatforms.length === 0) {
        return <PageHeader pageTitle="New Custom Model" sx={{pb: 0}}>Set up your first integration in order to
            train a custom model.<Button component={RouterLink} endIcon={<LaunchIcon/>}
                                         to="/integrations">Integrations</Button></PageHeader>
    }

    const startDisabled = !selectedModel || !template || !name || !options || blocked || !formValid || saveInProgress
    let startDisabledReason: string | null = null
    if (!selectedModel) startDisabledReason = "Select a platform and base model"
    else if (!template) startDisabledReason = "Select a template"
    else if (!name) startDisabledReason = "Name required"
    else if (!options) startDisabledReason = "Please complete the form"
    else if (blocked) startDisabledReason = "Blocked by precheck"
    else if (!formValid) startDisabledReason = "Please complete the form"

    return (
        <>
            <PageHeader pageTitle="New Custom Model" sx={{pb: 0}}/>

            {!trainableModels && <CircularProgress/>}
            {trainableModels &&
                <Grid container spacing={4} sx={{pt: 2}} className={animations.defaultIn}>
                    <Grid item lg={6} md={6} xs={12}>
                        <Stack spacing={2}>
                            <TextField label="Name"
                                       aria-label="custom model name"
                                       autoComplete="custom model-name"
                                       value={name}
                                       onChange={e => setName(e.target.value)}
                            />

                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box flex={1}>
                                    <TemplateSelect templates={project.templates} value={template}
                                                    onChange={setTemplate}/>
                                </Box>
                                <Button variant="outlined"
                                        color="secondary"
                                        startIcon={<AddIcon/>}
                                        onClick={() => setNewTemplateModalOpen(true)}
                                >
                                    New
                                </Button>
                            </Stack>

                            {project.templates.length === 0 &&
                                <Typography variant="caption" color="text.secondary">
                                    No templates yet. Click "New" to create one.
                                </Typography>
                            }

                            {template &&
                                <Box sx={{pl: 2, pr: 2, maxHeight: 320, overflowY: "auto"}}>
                                    <TemplateRenderer templateParts={template} staticSystemPromptTreatment="show"/>
                                </Box>
                            }

                            <GroupedModelSelect
                                value={selectedModel}
                                onChange={handleModelSelected}
                                trainableOnly
                                label="Platform & Base Model"
                            />

                            {template &&
                                <>
                                    {platform === ModelPlatform.OPEN_AI &&
                                        <CustomModelFormOpenAi
                                            baseModels={trainableModels.filter(it => it.platform === ModelPlatform.OPEN_AI) as ModelSpecOpenAiJson[]}
                                            template={template}
                                            setOptions={setOptions}
                                            setFormValid={setFormValid}
                                        />
                                    }
                                    {platform === ModelPlatform.TOGETHER && selectedModel &&
                                        <CustomModelFormTogether
                                            template={template}
                                            baseModel={selectedModel as ModelSpecTogetherJson}
                                            setOptions={setOptions}
                                            setFormValid={setFormValid}
                                        />
                                    }
                                </>
                            }

                            <Tooltip title={startDisabledReason}>
                                <Box>
                                    <AsyncButton waiting={saveInProgress}
                                                 disabled={startDisabled}
                                                 endIcon={<PlayArrowIcon/>}
                                                 onClick={handleCreateFinetune}
                                    >
                                        Start training
                                    </AsyncButton>
                                </Box>
                            </Tooltip>

                            {saveError && <ErrorMessage error={saveError}/>}
                        </Stack>
                    </Grid>

                    <Grid item lg={4} md={6} sm={12}>
                        {options && platform && template &&
                            <PrecheckPanel key={platform}
                                           project={project}
                                           platform={platform}
                                           template={template}
                                           options={options}
                                           setBlocked={setBlocked}
                                           sx={{position: "sticky", top: 90}}
                            />
                        }
                    </Grid>
                </Grid>
            }

            <NewTemplateModal
                project={project}
                open={newTemplateModalOpen}
                setOpen={setNewTemplateModalOpen}
                onCreated={created => {
                    reloadProject()
                    setTemplate(created)
                }}
            />
        </>
    )
}

export default TrainCustomModelPage;
